import { type FormEvent, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useFieldErrors } from "@/components/Tools/use-field-errors";
import {
  type CalIntegration,
  useCreateCalIntegration,
  useUpdateCalIntegration,
} from "@/services/cal-integration.query";
import {
  CAL_MESSAGES,
  type CalField,
  type CalForm,
  type CalRefusal,
  calErrors,
  calRefusalOf,
  refusedCalFields,
} from "@/utils/cal-integration-rules";

interface CalIntegrationDialogProps {
  agentId: string;
  /** The connection to change, or null to connect one. Mounted open, once per
   * opening: the form starts from it every time. */
  connection: CalIntegration | null;
  onClose: () => void;
}

/**
 * Connect Cal.com to an agent, or change its connection. The key is
 * write-only, so an edit's key box starts empty and, left empty, keeps the
 * stored key. Nothing the function would refuse is sent; what only it and
 * Cal.com can refuse comes back under the field it is about, or above the
 * buttons, and the dialog stays open with what was typed.
 */
export function CalIntegrationDialog({ agentId, connection, onClose }: CalIntegrationDialogProps) {
  const create = useCreateCalIntegration();
  const update = useUpdateCalIntegration();
  const connecting = !connection;
  const [form, setForm] = useState<CalForm>(() => ({
    apiKey: "",
    eventTypeId: connection ? String(connection.cal_event_type_id) : "",
  }));
  const [saving, setSaving] = useState(false);
  const [refusal, setRefusal] = useState<CalRefusal>();
  const contentRef = useRef<HTMLDivElement>(null);

  const ruleErrors = calErrors(form, connecting);
  const { field, error, check } = useFieldErrors<CalField>({ ...ruleErrors, ...refusedCalFields(refusal, form) });

  const set = (name: CalField, value: string) => setForm((current) => ({ ...current, [name]: value }));

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // A refusal from an earlier try no longer applies to what is on screen now.
    setRefusal(undefined);
    if (!check(contentRef.current, ruleErrors)) return;
    setSaving(true);
    // Sent trimmed; an edit sends the key only when one was typed.
    const key = form.apiKey.trim();
    const eventType = { cal_event_type_id: Number(form.eventTypeId.trim()) };
    try {
      if (connection) {
        await update.mutateAsync({ connection, updates: key ? { cal_api_key: key, ...eventType } : eventType });
      } else {
        await create.mutateAsync({ agent_id: agentId, cal_api_key: key, ...eventType });
      }
      onClose();
    } catch (failure) {
      const fallback = connecting ? "Cal.com could not be connected." : "The Cal.com connection could not be saved.";
      setRefusal(calRefusalOf(failure, fallback, form));
    } finally {
      setSaving(false);
    }
  };

  // A save in flight finishes in view: closing now would hide whether it worked.
  const handleClose = () => {
    if (!saving) onClose();
  };

  return (
    <Dialog open onOpenChange={handleClose}>
      <DialogContent ref={contentRef} className="sm:max-w-[500px] [&_[aria-invalid=true]]:border-destructive">
        <DialogHeader>
          <DialogTitle>{connecting ? "Connect Cal.com" : "Edit Cal.com connection"}</DialogTitle>
          <DialogDescription>
            Your agent will check availability and book appointments on this event type.
          </DialogDescription>
        </DialogHeader>

        <form noValidate onSubmit={handleSubmit} className="grid gap-4" data-testid="cal-form">
          <div className="grid gap-2">
            <Label htmlFor="cal-api-key">Cal.com API key</Label>
            <Input
              id="cal-api-key"
              data-testid="cal-api-key-input"
              type="password"
              autoComplete="off"
              placeholder="cal_live_xxxxxxxxxxxxx"
              value={form.apiKey}
              onChange={(event) => set("apiKey", event.target.value)}
              disabled={saving}
              {...field("apiKey", "cal-api-key")}
            />
            {error("apiKey", "cal-api-key")}
            {!connecting && <p className="text-xs text-muted-foreground">{CAL_MESSAGES.keyKept}</p>}
            <p className="text-xs text-muted-foreground">
              Get your API key from{" "}
              <a
                href="https://app.cal.com/settings/developer/api-keys"
                target="_blank"
                rel="noopener noreferrer"
                className="text-primary hover:underline"
              >
                Cal.com settings
              </a>
              .
            </p>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="cal-event-type">Event type ID</Label>
            <Input
              id="cal-event-type"
              data-testid="cal-event-type-input"
              inputMode="numeric"
              autoComplete="off"
              placeholder="123456"
              value={form.eventTypeId}
              onChange={(event) => set("eventTypeId", event.target.value)}
              disabled={saving}
              {...field("eventTypeId", "cal-event-type")}
            />
            {error("eventTypeId", "cal-event-type")}
            <p className="text-xs text-muted-foreground">It's in the URL of the event type's settings page.</p>
          </div>

          {refusal && !refusal.field && (
            <p role="alert" data-testid="cal-form-error" className="text-sm text-destructive">
              {refusal.message}
            </p>
          )}

          <DialogFooter>
            <Button type="button" variant="outline" onClick={handleClose} disabled={saving} data-testid="cal-cancel">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} aria-busy={saving} data-testid="cal-submit">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {saving ? (connecting ? "Connecting…" : "Saving…") : connecting ? "Connect" : "Save changes"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
